"use client";

import { useResolvedRewardsAccess } from "@/lib/hooks/use-resolved-rewards-access";
import { useOnboardingPermissions } from "@/subsystems/access/onboarding/hooks/use-onboarding-permissions";

export type AccountType = "has-mint-redeem" | "neither" | "vendor-only";

/** Which kind of customer this is. `neither` means onboarding is unfinished. */
function useAccountType(): AccountType {
  const permissions = useOnboardingPermissions();

  if (permissions?.isMintRedeemerApproved) return "has-mint-redeem";
  if (permissions?.isVendorApproved) return "vendor-only";
  return "neither";
}

/**
 * This hook tells you which entitlements the Accounts screens offer.
 *
 * caveat: the showXXX decisions are scoped to the Accounts screens,
 * you should not use them outside Accounts unless you are inferring a decision based on what shows up in Accounts.
 */
export function useAccountEntitlementOptions(): { showMintIL: boolean; showRewards: boolean } {
  const accountType = useAccountType();
  const enrolled = useResolvedRewardsAccess() === "enrolled";

  switch (accountType) {
    // Changing rewards tracking addresses is a function available to strategic partner only customers,
    // even before they enroll in the rewards feature. So enrollment is not relevant.
    case "vendor-only":
      return { showMintIL: false, showRewards: true };

    // Anyone who has mint and redeem access: enrollment is what buys rewards.
    case "has-mint-redeem":
      return { showMintIL: true, showRewards: enrolled };

    // Onboarding is unfinished, so the provider is already redirecting. Nothing is wrong here and
    // nothing is worth reporting; show the old screens for the paint before the redirect lands.
    case "neither":
      return { showMintIL: true, showRewards: false };
  }
}
